import { FC, ReactNode } from "react";
import { LinkProps, useMatchRoute, useParams } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import {
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentCurrencyDollarIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  MapIcon,
  MapPinIcon,
  MegaphoneIcon,
  NewspaperIcon,
  SparklesIcon,
  TruckIcon,
  UsersIcon,
  WrenchIcon,
} from "@heroicons/react/20/solid";
import {
  SidebarHeading,
  SidebarItemAnchor,
  SidebarItemLink,
  SidebarLabel,
  SidebarSection,
} from "@/components/catalyst/sidebar";
import { InvoiceStatus } from "@/gql/graphql";

type AdminNavPath =
  | "/$tenant/$orgId/supplier/{-$supplierId}/organisations"
  | "/$tenant/$orgId/supplier/{-$supplierId}/suppliers"
  | "/$tenant/$orgId/supplier/{-$supplierId}/users"
  | "/$tenant/$orgId/supplier/{-$supplierId}/invoices"
  | "/$tenant/$orgId/supplier/{-$supplierId}/quotes"
  | "/$tenant/$orgId/supplier/{-$supplierId}/waitlists"
  | "/$tenant/$orgId/supplier/{-$supplierId}/ads"
  | "/$tenant/$orgId/supplier/{-$supplierId}/blogs"
  | "/$tenant/$orgId/supplier/{-$supplierId}/admin/cities"
  | "/$tenant/$orgId/supplier/{-$supplierId}/admin/trips"
  | "/$tenant/$orgId/supplier/{-$supplierId}/admin/default-rates"
  | "/$tenant/$orgId/supplier/{-$supplierId}/admin/agreements"
  | "/$tenant/$orgId/supplier/{-$supplierId}/admin/relocation-pages"
  | "/$tenant/$orgId/supplier/{-$supplierId}/admin/tools"
  | "/$tenant/$orgId/supplier/{-$supplierId}/agents"
  | "/$tenant/$orgId/supplier/{-$supplierId}/emails";

type AdminNavItem =
  | {
      label: string;
      to: AdminNavPath;
      search?: LinkProps["search"];
      icon: FC<{ className?: string }>;
      trailing?: ReactNode;
    }
  | {
      label: string;
      href: string;
      icon: FC<{ className?: string }>;
    };

type AdminNavGroup = {
  heading: string;
  items: AdminNavItem[];
};

function useAdminNavGroups(): AdminNavGroup[] {
  const t = useTranslations("navigation");

  return [
    {
      heading: t("accounts"),
      items: [
        {
          label: t("organisations"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/organisations",
          icon: BuildingOffice2Icon,
        },
        {
          label: t("suppliers"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/suppliers",
          icon: TruckIcon,
        },
        {
          label: t("users"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/users",
          icon: UsersIcon,
        },
        {
          label: t("invoices"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/invoices",
          search: {
            invoiceStatus: [InvoiceStatus.Issued, InvoiceStatus.Overdue],
          },
          icon: DocumentCurrencyDollarIcon,
        },
        {
          label: t("quotes"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/quotes",
          icon: ClipboardDocumentListIcon,
        },
      ],
    },
    {
      heading: t("customers"),
      items: [
        {
          label: t("waitlists"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/waitlists",
          icon: ClockIcon,
        },
      ],
    },
    {
      heading: t("marketing"),
      items: [
        {
          label: t("ads"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/ads",
          icon: MegaphoneIcon,
        },
        {
          label: t("blog"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/blogs",
          icon: NewspaperIcon,
        },
        {
          label: t("relocationPages"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/relocation-pages",
          icon: DocumentDuplicateIcon,
        },
      ],
    },
    {
      heading: t("configuration"),
      items: [
        {
          label: t("cities"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/cities",
          icon: MapPinIcon,
        },
        {
          label: t("trips"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/trips",
          icon: MapIcon,
        },
        {
          label: t("defaultRates"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/default-rates",
          icon: CurrencyDollarIcon,
        },
        {
          label: t("agreements"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/agreements",
          icon: DocumentTextIcon,
        },
      ],
    },
    {
      heading: t("tools"),
      items: [
        {
          label: t("tools"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/admin/tools",
          icon: WrenchIcon,
        },
        {
          label: t("crm"),
          href: "https://crm.imoova.com",
          icon: ChartBarIcon,
        },
      ],
    },
    {
      heading: t("logs"),
      items: [
        {
          label: t("agents"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/agents",
          icon: SparklesIcon,
        },
        {
          label: t("emails"),
          to: "/$tenant/$orgId/supplier/{-$supplierId}/emails",
          icon: EnvelopeIcon,
        },
      ],
    },
  ];
}

function useIsAdminPathCurrent() {
  "use no memo";

  const matchRoute = useMatchRoute();
  const { supplierId } = useParams({ strict: false }) as {
    supplierId?: string;
  };

  // `{-$supplierId}` is optional, so `/supplier/settings/suppliers` would
  // otherwise match `/supplier/{-$supplierId}/suppliers` with supplierId "settings".
  return (to: AdminNavPath) => {
    const match = matchRoute({ to, fuzzy: true });
    return !!match && match.supplierId === supplierId;
  };
}

export function useIsAdminRoute() {
  "use no memo";

  const isCurrent = useIsAdminPathCurrent();
  const groups = useAdminNavGroups();

  return groups.some((group) =>
    group.items.some((item) => "to" in item && isCurrent(item.to)),
  );
}

export function AdminNavigation() {
  "use no memo";

  const isCurrent = useIsAdminPathCurrent();
  const groups = useAdminNavGroups();

  return groups.map((group) => (
    <SidebarSection key={group.heading}>
      <SidebarHeading>{group.heading}</SidebarHeading>
      {group.items.map((item) =>
        "href" in item ? (
          <SidebarItemAnchor
            key={item.label}
            href={item.href}
            target="_blank"
            rel="noreferrer"
          >
            <item.icon className="size-5" />
            <SidebarLabel>{item.label}</SidebarLabel>
            <ArrowTopRightOnSquareIcon className="ml-auto size-4 opacity-60" />
          </SidebarItemAnchor>
        ) : (
          <SidebarItemLink
            key={item.label}
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to={item.to}
            params={(prev) => prev}
            search={item.search ?? {}}
            current={isCurrent(item.to)}
            preload="intent"
            viewTransition
          >
            <item.icon className="size-5" />
            <SidebarLabel>{item.label}</SidebarLabel>
            {item.trailing}
          </SidebarItemLink>
        ),
      )}
    </SidebarSection>
  ));
}
